import React, { useState, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Stack,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import { Formik, Form, useFormikContext } from "formik";
import { usePaintCoatDetails } from "../hooks/usePaintCoatDetails";
import AmbientSurfaceConditionTable from "./AmbientSurfaceConditionTable";
import CoatingIdentification from "./CoatingIdentification";
import PaintingChecklist from "./PaintingChecklist";
import StageFooter from "./StageFooter";
import SkipStageComponent from "./PaintingCoats/SkipStageComponent";
import CreateUpdatePaintingTemplateForm from "./Fabrication/CreateUpdatePaintingTemplateForm";
import toast from "react-hot-toast";
import StageInactive from "./ViewRFI/StageInactive";

// Validation is provided by the usePaintCoatDetails hook

// Weather condition component that uses Formik context
const WeatherConditionSection = ({ viewOnly }) => {
  const { values, errors, touched, setFieldValue } = useFormikContext();

  const selectedValue =
    values && typeof values.weatherCondition === "object"
      ? Object.entries(values.weatherCondition).find(([, v]) => v)?.[0] || ""
      : values?.weatherCondition || "";

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Typography variant="body1" component="span" sx={{ minWidth: "120px" }}>
        Weather condition
      </Typography>
      <FormControl
        sx={{ mt: 0 }}
        error={Boolean(touched?.weatherCondition && errors?.weatherCondition)}
      >
        <FormLabel sx={{ display: "none" }}>Weather condition</FormLabel>
        <RadioGroup
          row
          value={selectedValue}
          onChange={(e) => setFieldValue("weatherCondition", e.target.value)}
        >
          <FormControlLabel
            value="clear"
            control={<Radio size="small" />}
            label="Clear"
            disabled={viewOnly}
          />
          <FormControlLabel
            value="rain"
            control={<Radio size="small" />}
            label="Rain"
            disabled={viewOnly}
          />
          <FormControlLabel
            value="cloudy"
            control={<Radio size="small" />}
            label="Cloudy"
            disabled={viewOnly}
          />
        </RadioGroup>
        {errors?.weatherCondition && touched?.weatherCondition && (
          <FormHelperText error sx={{ mt: 1, ml: 0 }}>
            {errors.weatherCondition}
          </FormHelperText>
        )}
      </FormControl>
    </Stack>
  );
};

/**
 * PaintCoatDetails - Component for managing paint coat application and inspection with Formik validation
 */
const PaintCoatDetails = ({
  coatType,
  formEnabled,
  showAction,
  showSubmitButton,
  awaitingReview,
  stage,
  isAPICallAllowed,
  isActive,
}) => {
  // Use shared hook for schema, initial values and handlers
  const {
    validationSchema,
    initialValues,
    stageLoading,
    skipStageLoading,
    handleFormSubmit,
    buildHandleSendForApproval,
    handleSkipStage,
    paintCoatDetails,
  } = usePaintCoatDetails({
    coatType,
    stageId: stage.stage_id,
    stage_number: stage.stage_number,
    isAPICallAllowed,
  });

  // Prepare template data for the modal
  const templateData = useMemo(() => {
    // Check if we have the required data
    if (!paintCoatDetails || !paintCoatDetails.painting_checklist_parts) {
      return null;
    }

    // Map images - ImageUploadSection expects objects with image_url property
    const mappedImages = (paintCoatDetails.painting_checklist_images || []).map(
      (img) => ({
        image_id: img.image_id,
        rfi_id: img.rfi_id,
        painting_checklist_template_id: img.painting_checklist_template_id,
        image_url: img.image_url,
      })
    );

    // Map the parts data to match the expected format for the form
    const mappedParts = (paintCoatDetails.painting_checklist_parts || []).map(
      (part) => ({
        painting_checklist_part_id: part.painting_checklist_part_id,
        rfi_id: part.rfi_id,
        coat_type: part.coat_type,
        part_name: part.part_name,
        part_location: part.part_location,
        painting_checklist_template_id: part.painting_checklist_template_id,
        created_by: part.created_by,
        painting_part_items: (part.painting_part_items || []).map((item) => ({
          painting_checklist_part_item_id: item.painting_checklist_part_item_id,
          painting_checklist_part_id: item.painting_checklist_part_id,
          item_name: item.item_name,
          visual_check: item.visual_check ?? false,
          remark: item.remark ?? "",
          created_by: item.created_by,
        })),
      })
    );

    // Create the template data object
    // Note: Since there's no template metadata in the API response, we set IDs to null
    const mappedData = {
      painting_checklist_template_id: null, // No template ID in RFI data
      model_id: null, // No model info in RFI data
      model: null,
      painting_checklist_template_images: mappedImages,
      painting_checklist_template_parts: mappedParts,
    };

    return mappedData;
  }, [paintCoatDetails]);

  // State for template edit modal
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateFormKey, setTemplateFormKey] = useState(0);
  const [currentTemplateData, setCurrentTemplateData] = useState(null);
  const formikValuesRef = useRef(null);
  const setFieldValueRef = useRef(null);

  // Handle opening template edit modal
  const handleEditTemplate = useCallback(() => {
    // Get current form values from ref (these are the latest edited values)
    const currentImages = formikValuesRef.current?.paintingChecklist?.images;
    const currentParts = formikValuesRef.current?.paintingChecklist?.parts;

    // If we have current form values, use them; otherwise fall back to templateData
    if (currentImages && currentParts) {
      const freshTemplateData = {
        painting_checklist_template_id: null,
        model_id: null,
        model: null,
        painting_checklist_template_images: currentImages,
        painting_checklist_template_parts: currentParts,
      };
      setCurrentTemplateData(freshTemplateData);
    } else if (templateData) {
      setCurrentTemplateData(templateData);
    } else {
      return;
    }

    setTemplateModalOpen(true);
    setTemplateFormKey((prev) => prev + 1);
  }, [templateData]);

  // Handle closing template edit modal
  const handleCloseTemplateModal = useCallback(() => {
    setTemplateModalOpen(false);
  }, []);

  // Handle template update success
  const handleTemplateUpdate = useCallback(async (formValues) => {
    try {
      // Reverse mapping: Convert form data back to paintCoatDetails format
      const updatedImages = formValues.painting_checklist_template_images || [];

      const updatedParts = (
        formValues.painting_checklist_template_parts || []
      ).map((part) => ({
        painting_checklist_part_id: part.painting_checklist_part_id,
        rfi_id: part.rfi_id,
        coat_type: part.coat_type,
        part_name: part.part_name,
        part_location: part.part_location,
        painting_checklist_template_id: part.painting_checklist_template_id,
        created_by: part.created_by,
        painting_part_items: (part.painting_part_items || []).map((item) => ({
          painting_checklist_part_item_id: item.painting_checklist_part_item_id,
          painting_checklist_part_id: item.painting_checklist_part_id,
          item_name: item.item_name,
          visual_check: item.visual_check ?? false,
          remark: item.remark ?? "",
          created_by: item.created_by,
        })),
      }));

      // Update the Formik state with the new values
      if (setFieldValueRef.current) {
        setFieldValueRef.current("paintingChecklist.images", updatedImages);
        setFieldValueRef.current("paintingChecklist.parts", updatedParts);
      }

      toast.success("Template updated successfully!");
      setTemplateModalOpen(false);
    } catch (error) {
      console.error("Error updating template:", error);
      toast.error("Failed to update template");
    }
  }, []);
  return (
    <>
      {isActive ? (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            setTouched,
            validateForm,
            submitForm,
            isSubmitting,
          }) => {
            // Store Formik values and setFieldValue in refs
            formikValuesRef.current = values;
            setFieldValueRef.current = setFieldValue;

            return (
              <Form>
                {stageLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "260vh",
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <Box>
                      {coatType === "second" && (
                        <SkipStageComponent
                          handleSkipStage={handleSkipStage}
                          loading={skipStageLoading}
                          stage={stage}
                        />
                      )}
                      <WeatherConditionSection
                        viewOnly={!formEnabled || stage.is_skipped}
                      />

                      <Box sx={{ mt: 3 }}>
                        <AmbientSurfaceConditionTable
                          values={values}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                          viewOnly={!formEnabled || stage.is_skipped}
                          stageType="painting"
                        />
                      </Box>

                      <Box sx={{ mt: 3 }}>
                        <CoatingIdentification
                          viewOnly={!formEnabled || stage.is_skipped}
                          values={values}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                        />
                      </Box>

                      <Box sx={{ mt: 3 }}>
                        <PaintingChecklist
                          viewOnly={!formEnabled || stage.is_skipped}
                          coatType={coatType}
                          values={values}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                          onEditTemplate={
                            formEnabled && !stage.is_skipped
                              ? handleEditTemplate
                              : undefined
                          }
                        />
                      </Box>
                    </Box>
                    <StageFooter
                      showAction={
                        (showAction &&
                          stage.current_step === "Inspector" &&
                          stage.stage_number === "Stage_5") ||
                        (showAction && stage.stage_number !== "Stage_5")
                      }
                      showSubmitButton={showSubmitButton}
                      onSendForApproval={
                        formEnabled &&
                        buildHandleSendForApproval(
                          validateForm,
                          submitForm,
                          setTouched,
                          values
                        )
                      }
                      submitButtonText={
                        stage.stage_number === "Stage_5"
                          ? "Submit"
                          : "Send for approval to QC Coordinator"
                      }
                      isSubmitting={isSubmitting}
                      awaitingReview={awaitingReview}
                      stage={stage}
                    />
                  </>
                )}
              </Form>
            );
          }}
        </Formik>
      ) : (
        <StageInactive />
      )}

      {/* Template Edit Modal */}
      {currentTemplateData && (
        <CreateUpdatePaintingTemplateForm
          key={templateFormKey}
          open={templateModalOpen}
          onClose={handleCloseTemplateModal}
          onSave={handleTemplateUpdate}
          initialData={currentTemplateData}
          updateFromStage={true}
        />
      )}
    </>
  );
};

export default PaintCoatDetails;
